import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import { useCallback, useEffect, useState } from 'react'
import * as React from 'react'
import { connect } from 'react-redux'
import TextInput from 'components/Common/Form/Input/TextInput'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TextButton from 'LuxKit/Button/TextButton'
import { formToObject } from 'lib/forms/formToObject'
import { formatPhoneNumber } from 'lib/format/formatPhoneNumber'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { sendSmsCode } from 'api/auth'
import { verifySmsCodeAction } from 'actions/AuthActions'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextLink from 'LuxKit/TextLink'
import useInterval from 'hooks/useInterval'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import { FormattedMessage, useIntl } from 'react-intl'
import { isOtpVerified } from 'lib/jwtToken/checkOtpVerification'
import { useAppDispatch } from 'hooks/reduxHooks'
import { trackClientEvent } from 'analytics/analytics'

const COUNTDOWN_SECONDS = 30

type ModalState = 'initial' | 'validating' | 'error'

interface OwnProps {}

interface MappedStateProps {
  phonePrefix?: string
  phone: string
}

type Props = OwnProps & MappedStateProps

function getErrorMessage(intl: ReturnType<typeof useIntl>, status?: number, message?: string) {
  switch (status) {
    case 429:
      return intl.formatMessage({
        id: 'Q8BFh1qb6s4kGmYTKTUT',
        defaultMessage: 'You’re going too fast! Please try again in 30 seconds.',
        description: 'Error notification displayed when OTP requests are rate limited',
      })
    case 422:
      return intl.formatMessage({
        id: 'baH0ILxZXBhaaiuDfMAn',
        defaultMessage: 'The code you entered is incorrect or has expired.',
        description: 'Error notification displayed when OTP code is invalid or expired',
      })
    default:
      return message ?? intl.formatMessage({
        id: 'iao0TxICbGXkByJhjgUd',
        defaultMessage: 'Unknown Error',
        description: 'Generic error message for OTP verification when an issue occurs',
      })
  }
}

function NavDeviceVerificationModal(props: Props) {
  const { phone, phonePrefix } = props
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const modalElement = useModalElementContext<'skipped' | 'verified' | 'closed'>()

  const [modalState, setModalState] = useState<ModalState>('initial')
  const [countDown, setCountDown] = useState<number>(COUNTDOWN_SECONDS)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  useInterval(
    () => { setCountDown((curr) => Math.max(curr - 1, 0)) },
    1000,
    { disabled: countDown <= 0 },
  )

  // Send OTP when modal opens
  useEffect(() => {
    const sendInitialOtp = async() => {
      await sendSmsCode()
    }
    sendInitialOtp().catch((error) => {
      const message = getErrorMessage(intl, error.status, error.message)
      setErrorMessage(message)
    })
  }, [intl])

  const handleSubmission = useCallback(async(e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setErrorMessage('')
    setModalState('validating')
    setIsLoading(true)

    const formData = formToObject<{ otpCode: string }>(e.currentTarget)
    try {
      const result = await dispatch(verifySmsCodeAction(formData.otpCode))
      if (isOtpVerified({ token: result.accessToken })) {
        await trackClientEvent({
          subject: 'submit_otp_button_successful',
          action: 'clicked',
          category: 'device_verification_modal',
          type: 'interaction',
        })

        modalElement.resolve('verified')
      } else {
        await trackClientEvent({
          subject: 'submit_otp_button_failed',
          action: 'clicked',
          category: 'device_verification_modal',
          type: 'interaction',
        })
        // skip the flow whenever we fail to receive the refreshed token - not to block customers from calling us
        modalElement.resolve('skipped')
        console.warn('Call verification flow skipped or error out - allowing customer to proceed as a guest.')
      }
    } catch(error: any) {
      const message = getErrorMessage(intl, error.status, error.message)
      setErrorMessage(message)
      setModalState('error')
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, modalElement, intl])

  const onResendCode = useCallback(async() => {
    setErrorMessage('')
    setCountDown(COUNTDOWN_SECONDS)
    setIsLoading(true)
    try {
      await sendSmsCode()
      showSnackbar(intl.formatMessage({
        id: '-t6TwAnRaNOSOGmFESWP',
        defaultMessage: 'Code has been sent to your phone number',
        description: 'Success message when OTP code is resent during the verification or authentication process',
      }), 'success')
    } catch(error: any) {
      const message = getErrorMessage(intl, error.status, error.message)
      setErrorMessage(message)
    } finally {
      setIsLoading(false)
    }
  }, [intl])

  const onClose = useCallback(() => {
    modalElement.resolve('closed')
  }, [modalElement])

  const onSkip = useCallback(() => {
    trackClientEvent({
      subject: 'skip_and_call_link',
      action: 'clicked',
      category: 'device_verification_modal',
      type: 'interaction',
    }).catch(reason => { console.error('Error tracking otp verification event at snowplow' + reason.message) })
    modalElement.resolve('skipped')
  }, [modalElement])

  const renderOtpForm = useCallback(
    (options: { errorDisplay: 'banner' | 'inline'; showInstructions: boolean }) => {
      const { errorDisplay, showInstructions } = options
      const resendDisabled = countDown > 0 || isLoading

      const resendCodeText = countDown > 0
        ? intl.formatMessage(
            {
              id: 'gKn5lJqvmFobiTeQpy0T',
              defaultMessage: 'Resend code in {seconds}s',
              description: 'Descriptive text for resend code link with countdown',
            },
            { seconds: countDown },
          )
        : intl.formatMessage({
            id: 'tp1yFkmOJybTRkT1wYEX',
            defaultMessage: 'Resend code',
            description: 'Link text to resend the verification code during the account access login process',
          })

      return (
        <VerticalSpacer gap={24} as="form" onSubmit={handleSubmission}>
          {errorMessage &&
            (errorDisplay === 'banner' ? (
              <MessageBanner kind="critical">{errorMessage}</MessageBanner>
            ) : (
              <BodyText variant="medium" colour="critical">{errorMessage}</BodyText>
            ))}
          {showInstructions && (
            <BodyText variant="medium">
              <FormattedMessage
                id="p-SZuOUVe8yK7hX8roNq"
                defaultMessage="Please enter 6-digit code sent to your phone number <strong>{phoneNumber}</strong>. This code will expire in 5 minutes."
                description="Instructions for entering the 6-digit OTP verification code"
                values={{
                  phoneNumber: formatPhoneNumber(phone, phonePrefix),
                  strong: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            </BodyText>
          )}
          <TextInput
            name="otpCode"
            inputMode="numeric"
            label={intl.formatMessage({
              id: '0ebjSsywxcxcKM58lQjk',
              defaultMessage: 'Enter a 6-digit code',
              description: 'Placeholder label for the OTP code input field in device verification',
            })}
            required
            minLength={6}
            maxLength={6}
            pattern="\d{6}"
            invalidErrorMessage={intl.formatMessage({
              id: 'mIWnd1nHiO4Zwk_6kF-X',
              defaultMessage: 'Enter 6 digits only',
              description: 'Error message shown when the OTP code format is invalid in device verification',
            })}
          />
          <Group direction="vertical" gap={16} horizontalAlign="center">
            <TextButton
              kind="primary"
              size="large"
              type="submit"
              fit="flex"
              disabled={isLoading}
            >
              {intl.formatMessage({
                id: 'jgQ7juUEqXtUMMLF1e-U',
                defaultMessage: 'Submit',
                description: 'Submit button for the OTP code in the device verification modal',
              })}
            </TextButton>
            <Group direction="horizontal" gap={8}>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'nidkrtawq440HYS_-bs0',
                  defaultMessage: "Didn't receive the code?",
                  description: 'Prompt text asking the user if they received the OTP verification code',
                })}
              </BodyText>
              <TextLink size="medium" onClick={onResendCode} disabled={resendDisabled}>
                {resendCodeText}
              </TextLink>
            </Group>
            <TextLink size="medium" onClick={onSkip}>
              <FormattedMessage
                id="8tHszniulK0O6RX7_v6Z"
                defaultMessage="Skip and call us now."
                description="Link to skip OTP verification and call the contact centre instead"
              />
            </TextLink>
          </Group>
        </VerticalSpacer>
      )
    },
    [
      errorMessage,
      countDown,
      isLoading,
      phone,
      phonePrefix,
      intl,
      handleSubmission,
      onResendCode,
      onSkip,
    ],
  )

  const renderValidatingState = () => (
    <VerticalSpacer gap={24}>
      <Group direction="vertical" gap={16} horizontalAlign="center">
        <LoadingSpinner size={40} />
        <BodyText variant="medium">
          <FormattedMessage
            id="ENC24GI1uYw4e0NXokLy"
            defaultMessage="Won't be long, we are validating your request."
            description="Loading message while validating OTP code with the contact centre"
          />
        </BodyText>
        <TextLink size="medium" onClick={onSkip}>
          <FormattedMessage
            id="HfrO1pJKGmoFMpJIspBJ"
            defaultMessage="Skip and call us now."
            description="Link to skip OTP validation and call the contact centre directly"
          />
        </TextLink>
      </Group>
    </VerticalSpacer>
  )

  const renderContent = () => {
    switch (modalState) {
      case 'initial':
        return renderOtpForm({ errorDisplay: 'banner', showInstructions: true })
      case 'validating':
        return renderValidatingState()
      case 'error':
        return renderOtpForm({ errorDisplay: 'inline', showInstructions: false })
    }
  }

  return (
    <ModalBase size="S">
      <ModalHeader
        title={intl.formatMessage({
          id: 'nNoA5M_mkFHH-JqQJtPQ',
          defaultMessage: 'Fast track your call by verifying your phone',
          description: 'Title for OTP verification modal to call customer support',
        })}
        onCloseButtonClick={onClose}
      />
      <ModalBody>
        <ModalContent>
          {renderContent()}
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

function mapStateToProps(state: App.State): MappedStateProps {
  return {
    phonePrefix: state.auth.account?.phonePrefix,
    phone: state.auth.account?.phone ?? '',
  }
}

export default connect(mapStateToProps)(NavDeviceVerificationModal)
